import { useEffect, useRef, useState } from "react";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { ChevronDown, ChevronRight } from "lucide-react";

import { DataTable, FilterBar, type FilterSpec } from "@/components/data";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { parseFilters } from "@/lib/filters";

import { DEFAULT_BASIS, gridQuery, isBadAsOf, isNoGrid, priceRunQuery, priceRunsQuery, type Basis, type GridResponse, type GridRow, type Market, type PriceRun, type ProposeAnswer, type RrpWritten, type TickedRow } from "./api";
import { ApplyPanel } from "./ApplyPanel";
import { AS_OF_COPY, asOfFromSearch, changeDatesOf, inForceOf, movedLine, otherDay, pickedDate, readThenPick, runDateOf, staleAsOf } from "./as_of";
import { AsOfControl, MovesOnLinks } from "./AsOfParts";
import { CommitBar } from "./CommitBar";
import { mergeIssues } from "./feed_issues";
import { FeedIssuesNote } from "./FeedIssuesNote";
import { OverridesSection } from "./OverridesSection";
import { overridesQuery, refreshOverrides } from "./overrides";
import {
  ABSENT, BASES, basisFromSearch, BASIS_URL, COPY, countryName, daysSince, earliestIso,
  humanDate, lastCommitLine, latestIso, MARKET_LABEL, messageFor, MISSING_LABEL, money, orders, PAGE_SIZE, pct1,
  REGION_KEYS, REGIONS, rowKey, sortFromSearch, SORTS, stamp, STORE_LABEL, type Region,
} from "./format";
import { configQuery, currencyFooter, type Setting } from "./config";
import { customerAtSaved, customerFigure, customerLookup, customerQuery, type CustomerReads } from "./customer";
import { partnersQuery } from "./discounts";
import {
  isPartnerView, notViewedCount, ORDER_TYPE_COPY, viewCustomerFigure, viewFromSearch, viewLabel, viewLookup, viewMargins, viewQuery, viewRefusal, type ViewReads,
} from "./order_views";
import { OrderTypeRow } from "./OrderTypeParts";
import { placeOf, savedLine, skuOf } from "./price";
import { afterGridSave, GridSaveCell, GridStepper, type GridEdit } from "./GridPriceEdit";
import { adsFromSearch, contributionCell, CONTRIBUTION_COPY } from "./contribution";
import { RowDetailSheet, type Carry } from "./RowDetailSheet";
import { gateRule, rrpQuery, type RrpReads } from "./rrp";
import { evalQuery, type EvalReads } from "./strategies";
import { badgeText, marketplaceNames, movedOff, PROPOSE_COPY, proposeChannel, proposeMarket, skippedGroups } from "./propose";
import { ProposeSheet, SkippedList } from "./ProposeSheet";
import { BandCell, BandLegend, HeaderInfo } from "./SignalParts";
import { competitionFigure, competitionReason } from "./competition";
import { cellBand, type RowBands, type SignalKey } from "./signal";
import { ConfigText } from "./WeeklyMovesSection";
import { liveFor, previewCell, useRowPreview } from "./row_preview";
import { cleanFilters, FILTER_FIELDS, findRow, focusKeyFor, keyMatches, options, pageOf, searchForFilters, sortGroups, visibleGroups, type MarginsOf, type VisibleRow } from "./rows";

const FILTER_LABEL: Record<string, string> = { product: "Product", variant: "Variant", channel: "Channel", store: "Store", location: "Location", marketplace: "Marketplace" };
// The bare Price Run opening follows Damien's ruling (DEC-1676, 2026-11-03).
const OPENING_REGION: Region = "all";
const OPENING_BASIS: Basis = "current_inventory_cogs";

export function PriceRunTab({ areaName }: { areaName: string }) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const go = (next: Record<string, unknown>) => void navigate({ to: ".", search: next, replace: true });
  const set = (patch: Record<string, unknown>) => go({ ...search, ...patch });
  const region: Region = REGION_KEYS.includes(search.pp_region as Region) ? (search.pp_region as Region) : OPENING_REGION;
  const markets = REGIONS[region].markets;
  // PP-01.3: the Contribution column reads the figure with or without ad spend; absent means Included.
  const ads = adsFromSearch(search.pp_ads);
  // The COGS basis driving every margin on screen (CL-019, C1 v1.10); only an absent key uses the Price Run opening basis.
  const basis: Basis = search.pp_basis === undefined ? OPENING_BASIS : basisFromSearch(search.pp_basis);
  // The as-of date (C1 v1.24, C2 v1.26): the URL's date asks C2 for the set in force on it, on every market on screen. C2 judges
  // the range; a date on or before the run date reads today's set and shows as today (`pickedDate`).
  const asked = asOfFromSearch(search.pp_asof);

  const queries = useQueries({ queries: markets.map((market) => gridQuery(market, basis, asked)) });
  // The open runs and the newest committed run per market (C2 v1.3). A failure here hides the batch, never the grid.
  const runs = useQuery(priceRunsQuery()).data;
  // The price overrides (C1 v1.50, C2 v1.52): the chip on a row whose committed price is a temporary one, and the section under the
  // commit bar. A failure is named in the section; it never hides the grid.
  const overrides = useQuery(overridesQuery()).data;
  const lastQueries = useQueries({ queries: (runs?.last_committed ?? []).filter((lc) => markets.includes(lc.market)).map((lc) => priceRunQuery(lc.run_id)) });
  const lastRuns: PriceRun[] = lastQueries.flatMap((q) => (q.data ? [q.data] : []));
  // The RRP overlay per market on view (C2 v1.8). A failure is named in the cell and the drawer; it never hides the grid.
  const rrpQueries = useQueries({ queries: markets.map((market) => rrpQuery(market)) });
  const rrps: RrpReads = Object.fromEntries(markets.map((market, i) => [market, { overlay: rrpQueries[i].data ?? null, failed: Boolean(rrpQueries[i].error) }]));
  const gate = markets.map((market) => rrps[market]?.overlay?.gate).find(Boolean) ?? null;
  // The strategy evaluation per market on the basis on view (C2 v1.14): the rules chip and the drawer's Strategy section. A
  // failure is named in the chip; it never hides the grid. On a picked date it is asked for the URL's date, as the grid is
  // (C1 v1.25, C2 v1.27), so the chips are judged at the fees in force on it.
  const evalQueries = useQueries({ queries: markets.map((market) => evalQuery(market, basis, asked)) });
  const evals: EvalReads = Object.fromEntries(markets.map((market, i) => [market, { eval: evalQueries[i].data ?? null, failed: Boolean(evalQueries[i].error) }]));
  // The Customer lens per market at the committed prices (C2 v1.15): the Customer column's figure and the drawer's lens. A failure
  // reads `no data` in the cell and is named in the drawer's lens; it never hides the grid.
  const customerQueries = useQueries({ queries: markets.map((market) => customerQuery(market)) });
  const customers: CustomerReads = Object.fromEntries(markets.map((market, i) => [market, { lens: customerQueries[i].data ?? null, failed: Boolean(customerQueries[i].error) }]));
  // The order type (C1 v1.23, CL-029): Single order is the grid at the list price, as before; Subscription or a partner programme
  // re-reads every row at the view's net price (C2 v1.23). A failed view read is named above the table and its rows keep the
  // grid's figures, never hidden. The chips' programmes are the Discounts read's.
  const view = viewFromSearch(search.pp_view);
  const partners = useQuery(partnersQuery());
  const viewQueries = useQueries({ queries: markets.map((market) => ({ ...viewQuery(market, view ?? "subscription", basis, asked), enabled: view !== null })) });
  const views: ViewReads | null = view === null ? null : Object.fromEntries(markets.map((market, i) => [market, { view: viewQueries[i].data ?? null, error: viewQueries[i].error }]));
  const label = viewLabel(view, partners.data?.programmes, view === null ? [] : viewQueries.map((q) => q.data ?? null));
  const lookupOf = (v: VisibleRow) => viewLookup(views, v.market, skuOf(v.row), placeOf(v.row));
  // Sorted and flagged by the figures on screen: in a view the view's at the net, none for a row the view does not price.
  const marginsOf: MarginsOf | undefined = views === null ? undefined : (v) => {
    const vl = lookupOf(v);
    if (vl.kind === "off") return v.row.margins;
    return vl.kind === "row" && vl.row.figures ? viewMargins(vl.row.figures, v.row.location, v.child)?.margins ?? null : null;
  };
  // The gross margin target is the Config value (C2 v1.7 `GET /api/config`), never a constant (C1 v1.9). Undefined while it
  // loads, null when unset: no row is flagged and the legend says so. A failure here leaves the grid unflagged, never hidden.
  const config = useQuery(configQuery());
  const targetSetting: Setting | null = config.data?.settings.margin_target_pct ?? null;
  // The commit the apply panel shows: its runs and the RRPs it wrote (C1 v1.42).
  const [applied, setApplied] = useState<{ committed: PriceRun[]; rrps: RrpWritten[] } | null>(null);
  // The grid's own price edit (C1 v1.57, T25): the one row in edit, and a save carried into the drawer (GATE-1084 PS-2 A).
  const [edit, setEdit] = useState<GridEdit | null>(null);
  const [carry, setCarry] = useState<Carry | null>(null);
  // The strategy run (c1-run-propose, DEC-1364 C): the ticked rows (screen state, never in the URL), the sheet, and the last real
  // pass, whose skipped list sits under the table until the region or channel changes. The Channel chip is the grid's own
  // Channel filter (`f_channel`), so the chip, the dropdown and the run read one value.
  const channelFilter = typeof search.f_channel === "string" ? search.f_channel : null;
  const [ticks, setTicks] = useState<Map<string, TickedRow>>(() => new Map());
  const [proposeOpen, setProposeOpen] = useState(false);
  const [lastPass, setLastPass] = useState<ProposeAnswer | null>(null);
  const [showSkipped, setShowSkipped] = useState(false);
  const proposeButton = useRef<HTMLButtonElement>(null);
  useEffect(() => { setTicks(new Map()); setLastPass(null); setShowSkipped(false); }, [region, channelFilter]);
  // A grid once read stays on screen when a refetch fails (C1 v1.17): the read is named and Retry refetches; only a market that
  // never loaded is an error, and only a no_grid_yet answer is a missing market.
  const states = queries.map((q) => (q.data ? "ok" : q.error ? (isNoGrid(q.error) ? "none" : "error") : "pending"));
  const hardError = queries.find((_, i) => states[i] === "error")?.error;
  const grids: GridResponse[] = queries.flatMap((q, i) => (states[i] === "ok" && q.data ? [q.data] : []));
  const placeNames = marketplaceNames(grids);
  const noneMarkets = markets.filter((_, i) => states[i] === "none");
  const failedRefresh = queries.filter((q) => q.data && q.error);
  const retry = (which: typeof queries) => { for (const q of which) void q.refetch(); };
  // Every control below that changes nothing carries `data-nonmutating` (AGENTS rule 11, bug 3eb0402f-c6c9-816d): Retry, the Region
  // and basis chips, the missing filter, the sort, Detail and Show/Hide countries all work for a view-only person; Save and Commit do not.
  const retryButton = (which: typeof queries) => <Button size="sm" variant="outline" className="ml-2" data-nonmutating onClick={() => retry(which)}>{COPY.retry}</Button>;

  // The dates on screen, from whatever grid has been read: the run date bounds the picker, the picked date and the set in force
  // on it come from C2's envelope. Before any read, the URL's date shows in the picker. While a picked date is read (or when C2
  // refuses it), the run date and the change dates come from today's read in the cache, so the picker keeps its bounds and the
  // links stay on screen, and nothing is said about the set in force until C2 answers (review round 2).
  const client = useQueryClient();
  const loaded = queries.flatMap((q) => (q.data ? [q.data] : []));
  const known = loaded.length > 0 ? loaded : markets.flatMap((market) => {
    const cached = client.getQueryData<GridResponse>(gridQuery(market, basis).queryKey);
    return cached ? [cached] : [];
  });
  const runDate = runDateOf(known);
  const asOf = loaded.length > 0 ? pickedDate(loaded, runDate) : asked;
  const inForceFrom = inForceOf(loaded, runDate);
  const dateUnread = asked !== null && loaded.length === 0;
  const pickDate = (next: string | null) => set({ pp_asof: next ?? undefined });
  // A URL date C2 answered as today leaves the URL (a replace, no history entry), so the link matches the screen (review round 1).
  const stale = staleAsOf(asked, loaded, runDate);
  // The chips and the view are read on the URL's date too (C1 v1.25). C2 v1.27 judges them at the fees of the picked date after
  // its own today and at today's on or before it (a grid two or more days old), and an API before v1.27 at today's on any date:
  // a read that answered another day than the one on screen names it in the banner, never the date it was not read on. A market's
  // evaluation counts only where its chips are on screen: in a view the chips are the view's, unless the view could not be read
  // there (review round 1).
  const chipsDay = otherDay(evalQueries.map((q, i) => (view === null || (viewQueries[i].error && !viewQueries[i].data) ? q.data?.as_of : null)), asOf);
  const viewDay = view === null ? null : otherDay(viewQueries.map((q) => q.data?.as_of), asOf);
  useEffect(() => { if (stale) pickDate(null); }, [stale]);
  const preview = useRowPreview(edit, grids, basis, asOf, view);
  const edited = edit ? findRow(grids, edit.key) : null;

  const describedBy = (key: string) => `pp-basis-${key}`;
  // The button shows on one market at a time, and on AE only once a strategy runs there (DEC-1403 D3, §9.10 item 4). It is
  // primary while the market has no saved lines. It carries no `data-nonmutating`, so a viewer's shell renders it inert (AGENTS rule 11).
  const proposeTo = proposeMarket(markets, Object.fromEntries(markets.map((m) => [m, evals[m]?.eval?.strategised ?? false])));
  const hasSaved = (runs?.runs ?? []).some((r) => r.market === proposeTo && r.state === "open" && r.lines.some((l) => l.state === "saved"));
  const channelChip = (
    <div role="group" aria-labelledby="pp-channel-chip" className="flex flex-wrap items-center gap-1">
      <span id="pp-channel-chip" className="text-sm text-ks-grey-1">{PROPOSE_COPY.channel}</span>
      {([null, "Amazon", "Shopify"] as const).map((c) => (
        <Button key={c ?? "all"} size="sm" variant={c === channelFilter ? "default" : "outline"} aria-pressed={c === channelFilter} data-nonmutating data-channel-chip={c ?? "All"}
          onClick={() => set({ f_channel: c ?? undefined, pp_page: undefined })}>
          {c ?? "All"}
        </Button>
      ))}
    </div>
  );
  const toolbar = (
    <div className="flex flex-wrap items-center gap-3">
      <div role="group" aria-label="Region" className="flex gap-1">
        {REGION_KEYS.map((key) => (
          <Button key={key} size="sm" variant={key === region ? "default" : "outline"} aria-pressed={key === region} data-nonmutating
            onClick={() => set({ pp_region: key, pp_page: undefined, pp_open: undefined, pp_row: undefined })}>
            {REGIONS[key].label}
          </Button>
        ))}
      </div>
      <TooltipProvider>
        {/* T29 (audit P12, UX v0.27 §7 basis switch): the toggle is named "Margin basis", on screen and to a screen reader. */}
        <div role="group" aria-labelledby="pp-margin-basis" className="flex flex-wrap items-center gap-1">
          <span id="pp-margin-basis" className="text-sm text-ks-grey-1">{COPY.marginBasis}</span>
          {BASES.map((b) => b.off ? (
            <Tooltip key={b.key}>
              <TooltipTrigger asChild>
                <span tabIndex={0} aria-describedby={describedBy(b.key)}><Button size="sm" variant="outline" disabled>{b.label}</Button></span>
              </TooltipTrigger>
              <TooltipContent>{b.off}</TooltipContent>
            </Tooltip>
          ) : (
            // Switching the basis keeps the region, filters and open drawer (row keys are the same on every basis) and resets the page.
            <Button key={b.key} size="sm" variant={b.key === basis ? "default" : "outline"} aria-pressed={b.key === basis} data-nonmutating
              onClick={() => set({ pp_basis: BASIS_URL[b.key as Basis], pp_page: undefined })}>
              {b.label}
            </Button>
          ))}
        </div>
      </TooltipProvider>
      {BASES.filter((b) => b.off).map((b) => <span key={b.key} id={describedBy(b.key)} className="sr-only">{b.off}</span>)}
      <div role="group" aria-labelledby="pp-ads-mode" className="flex flex-wrap items-center gap-1">
        <span id="pp-ads-mode" className="text-sm text-ks-grey-1">{CONTRIBUTION_COPY.adsToggle}</span>
        {(["included", "excluded"] as const).map((m) => (
          <Button key={m} size="sm" variant={m === ads ? "default" : "outline"} aria-pressed={m === ads} data-nonmutating
            onClick={() => set({ pp_ads: m === "included" ? undefined : m })}>
            {m === "included" ? CONTRIBUTION_COPY.included : CONTRIBUTION_COPY.excluded}
          </Button>
        ))}
      </div>
      {channelChip}
      <AsOfControl asOf={asOf} runDate={runDate} inForceFrom={inForceFrom} unread={dateUnread} onChange={pickDate} />
      {proposeTo && (
        <Button ref={proposeButton} size="sm" variant={hasSaved ? "outline" : "default"} data-propose-button title={PROPOSE_COPY.button} onClick={() => setProposeOpen(true)}>
          {ticks.size > 0 ? PROPOSE_COPY.saveTicked(ticks.size) : PROPOSE_COPY.button}
        </Button>
      )}
    </div>
  );
  // Switching the order type keeps the region, basis, filters, page and open drawer: the rows are the same, the figures change.
  const controls = (
    <div className="flex flex-col gap-2">
      {toolbar}
      <MovesOnLinks dates={changeDatesOf(known)} asOf={asOf} onPick={pickDate} />
      <OrderTypeRow view={view} programmes={partners.data?.programmes ?? []} unread={partners.isError} onChange={(next) => set({ pp_view: next ?? undefined })} />
    </div>
  );
  const heading = (
    <div className="flex flex-wrap items-baseline gap-3">
      <h2 className="text-xl font-semibold text-ks-ink">{COPY.title}</h2>
      <span className="text-sm text-ks-grey-1">{COPY.note}</span>
    </div>
  );

  if (hardError && isBadAsOf(hardError)) {
    return (
      <div className="flex flex-col gap-3">{heading}{controls}
        <p role="alert" className="text-ks-danger">{AS_OF_COPY.badDate}<Button size="sm" variant="outline" className="ml-2" data-nonmutating onClick={() => pickDate(null)}>{AS_OF_COPY.backToToday}</Button></p>
      </div>
    );
  }
  if (hardError) {
    return <div className="flex flex-col gap-3">{heading}{controls}<p role="alert" className="text-ks-danger">{messageFor(hardError, areaName)}{retryButton(queries.filter((q) => q.error && !q.data))}</p></div>;
  }
  if (states.includes("pending")) return <div className="flex flex-col gap-3">{heading}{controls}{Array.from({ length: 6 }, (_, i) => <Skeleton key={i} className="h-9" />)}</div>;
  if (grids.length === 0) {
    return (
      <div className="flex flex-col gap-3">{heading}{controls}
        <Card><CardContent className="py-10 text-center">
          <p className="text-lg font-semibold text-ks-ink">{basis === DEFAULT_BASIS ? COPY.emptyTitle : COPY.emptyTitleBasis(basis)}</p>
          <p className="text-ks-grey-1">{basis === DEFAULT_BASIS ? COPY.emptyBody : COPY.emptyBodyBasis}</p>
        </CardContent></Card>
      </div>
    );
  }

  const values = cleanFilters(grids, parseFilters(search));
  const onlyMissing = search.pp_missing === "1";
  const openStores = new Set(String(search.pp_open ?? "").split(",").filter(Boolean));
  const sort = sortFromSearch(search.pp_order);
  const groups = sortGroups(visibleGroups(grids, values, onlyMissing, openStores), sort, marginsOf);
  const page = pageOf(groups, Number(search.pp_page ?? 0) || 0);
  const active = FILTER_FIELDS.filter((f) => values[f]).length + (onlyMissing ? 1 : 0);
  // C1 v1.41: each dropdown offers only the values the other active filters leave (`options` with the values on view).
  const filterSpecs: FilterSpec[] = FILTER_FIELDS.map((f) => ({ name: f, label: FILTER_LABEL[f], kind: "choice", options: options(grids, f, values), anyLabel: "All" }));

  const toggleStore = (row: GridRow) => {
    const next = new Set(openStores);
    const matching = [...next].filter((key) => keyMatches(row, key));
    if (matching.length > 0) for (const key of matching) next.delete(key);
    else next.add(rowKey(row));
    set({ pp_open: [...next].join(",") || undefined });
  };
  const openRow = (key: string) => set({ pp_row: key });
  const closeRow = () => {
    const key = typeof search.pp_row === "string" ? search.pp_row : null;
    setCarry(null);
    set({ pp_row: undefined });
    // Focus returns to that row's product button however the sheet was opened (button, row click or a link).
    if (key) window.setTimeout(() => document.querySelector<HTMLElement>(`[data-row-key="${CSS.escape(focusKeyFor(grids, key))}"]`)?.focus(), 0);
  };

  // C1 v1.60 (GATE-1089): the open feed issues behind the grids on view and the saved lines, once each, above the table.
  const issues = mergeIssues(...grids.map((g) => g.envelope.feed_issues), runs?.feed_issues);
  const late = grids.filter((g) => g.envelope.stale);
  // C2 v1.18: stale follows the Config warn-after, one company value, so any late grid names it; null or absent reads as unset.
  const lateHours = late.map((g) => g.envelope.stale_basis_hours).find((h) => h != null) ?? null;
  const oldFeed = grids.find((g) => g.envelope.feeds.fulfilment.stale)?.envelope.feeds.fulfilment;
  const imported = latestIso(grids.map((g) => g.grid.prices_imported_at));
  const lastCommit = latestIso((runs?.last_committed ?? []).filter((lc) => markets.includes(lc.market)).map((lc) => lc.committed_at));
  const fxDate = latestIso(grids.map((g) => g.grid.fx.rate_date));
  const basisLine = COPY.basisLine(basis, region === "all" ? "all regions" : REGIONS[region].label);
  // The weekly Xero stock read (owner ruling Q13): one company-wide date on every envelope, absent on an API before C2 v1.4 named it.
  const xero = grids.map((g) => g.envelope.feeds.pool_cogs).find(Boolean);
  const xeroNote = xero === undefined ? null : xero.latest === null ? COPY.xeroNotRead : xero.stale ? COPY.xeroReadOld(humanDate(xero.latest)) : COPY.xeroRead(humanDate(xero.latest));
  const gridOf = (market: Market) => grids.find((g) => g.grid.market === market)?.grid;
  const footer = markets.map((m) => currencyFooter(config.data?.currency_links, m, gridOf(m))).filter((line): line is string => line !== null);
  // The table caption (v26): the order type, the basis and the day the margins read. While a view reads it says so; when no
  // market's view could be read the rows show Single order figures, and the caption says that (the alert above says why).
  const answered = view !== null && viewQueries.some((q) => q.data);
  const unread = view !== null && !answered && viewQueries.some((q) => q.error);
  // On a picked date the grid's margins are that date's, so Single order names it; a view names the day C2 read it on, the picked
  // date since C2 v1.27 (C1 v1.25).
  const caption = view === null || unread ? ORDER_TYPE_COPY.caption(ORDER_TYPE_COPY.single, basis, asOf ?? latestIso(grids.map((g) => g.grid.as_of)))
    : ORDER_TYPE_COPY.caption(label, basis, answered ? latestIso(viewQueries.map((q) => q.data?.as_of)) : null);

  const partner = isPartnerView(view);
  const currencyOf = (row: GridRow, market: Market) => row.price?.currency ?? row.currency ?? savedLine(runs, row, market)?.currency ?? null;
  const carryTo = (row: GridRow, value: string, refusal: Carry["refusal"]) => {
    const key = rowKey(row);
    setCarry({ key, value, refusal, n: (carry?.n ?? 0) + 1 });
    setEdit(null);
    openRow(key);
  };
  // Pure-signal rows (c1-price-run-signal-rows, CLB-022, DEC-1365): Product · Marketplace, the price in force, the New price stepper,
  // five band cells and Save (D3). Every sentence a cell carried is in the drawer; a click anywhere on the row opens it.
  // The bands are those of the figures on screen: the grid row's, or in an order-type view the view row's (E2). Contribution always
  // shows the grid's single-order figure, so it reads the grid row's band.
  const viewBands = (v: VisibleRow): RowBands | null | undefined => {
    const vl = lookupOf(v);
    if (vl.kind === "off") return v.row.bands;
    return vl.kind === "row" && !v.child ? vl.row.bands ?? null : null;
  };
  const shownMargins = (v: VisibleRow) => (marginsOf ? marginsOf(v) : v.row.margins);
  const live = (v: VisibleRow) => liveFor(v, preview, edited);
  const fromPreview = (v: VisibleRow, key: SignalKey) => {
    const l = live(v);
    return l?.p ? previewCell(l.p, key, v, view, ads) : undefined;
  };
  const entriesOf = (key: SignalKey) => page.rows.map((v) => cellBand(key === "contribution" ? v.row.bands : viewBands(v), key, ads));
  const head = (label: string, key: SignalKey) => () => <HeaderInfo label={label} entries={entriesOf(key)} />;
  // Row ticks (DEC-1364 C): a store or marketplace row, never a ship country (its price is its store's). A tick is a selection, so
  // a viewer may tick; only Propose writes.
  const tickable = (v: VisibleRow) => !v.child && v.row.grain !== "ship_country";
  const toggleTick = (row: GridRow, on: boolean) => setTicks((prev) => {
    const next = new Map(prev);
    if (on) next.set(rowKey(row), { xero_sku: skuOf(row), marketplace_id: placeOf(row) }); else next.delete(rowKey(row));
    return next;
  });
  const pageTickable = page.rows.filter(tickable);
  const allTicked = pageTickable.length > 0 && pageTickable.every((v) => ticks.has(rowKey(v.row)));
  const tickColumn: ColumnDef<VisibleRow, unknown> = {
    id: "tick", meta: { width: "3%" },
    header: () => (proposeTo ? <Checkbox aria-label={PROPOSE_COPY.tickAll} checked={allTicked} data-nonmutating
      onCheckedChange={(c) => { for (const v of pageTickable) toggleTick(v.row, c === true); }} /> : null),
    cell: ({ row: r }) => (proposeTo && tickable(r.original) ? (
      <div onClick={(e) => e.stopPropagation()}>
        <Checkbox aria-label={PROPOSE_COPY.tickRow(r.original.row.product, r.original.row.marketplace_name ?? r.original.row.channel_location)} checked={ticks.has(rowKey(r.original.row))}
          data-nonmutating data-row-tick={rowKey(r.original.row)} onCheckedChange={(c) => toggleTick(r.original.row, c === true)} />
      </div>
    ) : ""),
  };
  const columns: ColumnDef<VisibleRow, unknown>[] = [
    tickColumn,
    { id: "product", header: "Product · Marketplace", meta: { width: "20%" }, cell: ({ row: r }) => (
      <ProductCell v={r.original} onOpen={() => openRow(rowKey(r.original.row))} onToggle={() => toggleStore(r.original.row)} />
    ) },
    { id: "price", header: "Price", meta: { width: "8%" }, cell: ({ row: r }) => <PriceCell row={r.original.row} /> },
    { id: "new_price", header: "New price", meta: { width: "15%" }, cell: ({ row: r }) => {
      const v = r.original;
      if (v.child || v.row.grain === "ship_country") return "";
      const key = rowKey(v.row);
      const saved = savedLine(runs, v.row, v.market);
      // c1-run-propose (§9.10 NewPriceCell): a line the strategy run saved carries its badge under the value, and `strategy X` once
      // the person moved it. The badge reads the rule check saved with the line; it is text, never colour alone.
      const badge = badgeText(saved?.proposal);
      return (
        <div onClick={(e) => e.stopPropagation()}>
          <GridStepper row={v.row} value={edit?.key === key ? edit.value : (saved?.new_price ?? v.row.price?.value ?? "")} editing={edit?.key === key}
            currency={currencyOf(v.row, v.market)} preview={edit?.key === key ? preview : { kind: "none" }} onChange={(value) => setEdit({ key, value })} />
          {saved?.proposal && (
            <div className="mt-1 flex flex-wrap items-center gap-1 text-xs" data-proposal-cell>
              {badge && <Badge variant="outline" data-proposal-badge>{badge}</Badge>}
              {movedOff(saved) && <span className="text-ks-grey-1 tabular-nums" data-proposal-was>{PROPOSE_COPY.strategyWas(saved.proposal.price)}</span>}
            </div>
          )}
        </div>
      );
    } },
    // DEC-1413 A: in an order-type view the header names the single-order figure; the cell keeps the grid row's band, no note.
    { id: "contribution", header: head(view === null ? CONTRIBUTION_COPY.header(ads) : CONTRIBUTION_COPY.headerInView(ads), "contribution"), meta: { width: "10%" }, cell: ({ row: r }) => {
      const c = contributionCell(r.original.row.margins, ads);
      const figure = c.kind === "value" ? c.text : null;
      const entry = cellBand(r.original.row.bands, "contribution", ads);
      const pc = fromPreview(r.original, "contribution");
      const l = live(r.original);
      return <BandCell figure={pc ? pc.figure : figure} entry={pc ? pc.entry : entry} dim={l?.dim ?? false} live={Boolean(pc) && !l?.dim} />;
    } },
    { id: "fulfilment", header: head("Fulfilment", "fulfilment"), meta: { width: "9%" }, cell: ({ row: r }) => {
      const figure = shownMargins(r.original)?.fulfilment_pct == null ? null : pct1(shownMargins(r.original)?.fulfilment_pct);
      const entry = cellBand(viewBands(r.original), "fulfilment", ads);
      const pc = fromPreview(r.original, "fulfilment");
      const l = live(r.original);
      return <BandCell figure={pc ? pc.figure : figure} entry={pc ? pc.entry : entry} dim={l?.dim ?? false} live={Boolean(pc) && !l?.dim} />;
    } },
    { id: "gross", header: head("Gross", "gross"), meta: { width: "9%" }, cell: ({ row: r }) => {
      const figure = shownMargins(r.original)?.gross_pct == null ? null : pct1(shownMargins(r.original)?.gross_pct);
      const entry = cellBand(viewBands(r.original), "gross", ads);
      const pc = fromPreview(r.original, "gross");
      const l = live(r.original);
      return <BandCell figure={pc ? pc.figure : figure} entry={pc ? pc.entry : entry} dim={l?.dim ?? false} live={Boolean(pc) && !l?.dim} />;
    } },
    { id: "customer", header: head("Customer", "customer"), meta: { width: "9%" }, cell: ({ row: r }) => {
      if (r.original.child) return "";
      const inView = viewCustomerFigure(lookupOf(r.original));
      const lens = customerLookup(customers, r.original.market, skuOf(r.original.row), placeOf(r.original.row));
      const figure = inView !== undefined ? inView : customerFigure(lens);
      // C2 bands the figure at the committed price; a figure at a saved price paints neutral rather than borrow that band.
      const entry = inView === undefined && customerAtSaved(lens) ? null : cellBand(viewBands(r.original), "customer", ads);
      const pc = fromPreview(r.original, "customer");
      const l = live(r.original);
      return <BandCell figure={pc ? pc.figure : figure} entry={pc ? pc.entry : entry} dim={l?.dim ?? false} live={Boolean(pc) && !l?.dim} />;
    } },
    // c1-competition-cell (bug b85a3efc): C2's competitor figure (C2 v1.79), the view row's in a view; no figure names C2's reason.
    // c1-live-row-preview: the row in edit shows C2's comparison at the typed price instead (DEC-1662 A).
    { id: "competition", header: head("Competition", "competition"), meta: { width: "9%" }, cell: ({ row: r }) => {
      if (r.original.child) return "";
      const pc = fromPreview(r.original, "competition");
      const l = live(r.original);
      const lookup = lookupOf(r.original);
      const reason = pc ? null : competitionReason(r.original.row, lookup);
      return (
        <div title={reason ?? undefined} data-competition-reason={reason ?? undefined}>
          <BandCell figure={pc ? pc.figure : competitionFigure(r.original.row, lookup)} entry={pc ? pc.entry : cellBand(viewBands(r.original), "competition", ads)}
            dim={l?.dim ?? false} live={Boolean(pc) && !l?.dim} />
        </div>
      );
    } },
    { id: "save", header: "Save", meta: { width: "11%" }, cell: ({ row: r }) => {
      const v = r.original;
      if (v.child || v.row.grain === "ship_country") return "";
      const key = rowKey(v.row);
      return (
        <div onClick={(e) => e.stopPropagation()}>
          <GridSaveCell row={v.row} saved={savedLine(runs, v.row, v.market)} editing={edit?.key === key} value={edit?.key === key ? edit.value : ""} currency={currencyOf(v.row, v.market)}
            partner={partner} areaName={areaName} onDone={(sent) => setEdit((e) => afterGridSave(e, key, sent))} onCarry={(value, refusal) => carryTo(v.row, value, refusal)} />
        </div>
      );
    } },
  ];

  return (
    <div className="flex flex-col gap-3">
      {heading}
      {controls}
      <div className="flex flex-wrap items-center gap-3">
        <FilterBar layout="inline" filters={filterSpecs} values={values} onChange={(next) => go(searchForFilters(search, next))} />
        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={onlyMissing} data-nonmutating onCheckedChange={(c) => set({ pp_missing: c === true ? "1" : undefined, pp_page: undefined })} />
          {COPY.onlyMissing}
        </label>
        <label className="flex items-center gap-2 text-sm">
          {COPY.sortLabel}
          <Select value={sort ?? "grid"} onValueChange={(v) => set({ pp_order: v === "grid" ? undefined : v, pp_page: undefined })}>
            <SelectTrigger aria-label={COPY.sortLabel} data-nonmutating><SelectValue>{SORTS.find((s) => s.key === (sort ?? "grid"))?.label}</SelectValue></SelectTrigger>
            <SelectContent>{SORTS.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
        </label>
        {xeroNote && <span className="text-sm text-ks-grey-1">{xeroNote}</span>}
      </div>
      <p className="text-sm text-ks-grey-1">
        {lastCommit ? COPY.headerLog(humanDate(lastCommit), humanDate(fxDate), markets.includes("EU")) : COPY.header(humanDate(imported), humanDate(fxDate), markets.includes("EU"))}
      </p>
      {runs && <p className="text-sm text-ks-grey-1">{lastCommitLine(markets, runs.last_committed)}</p>}
      {failedRefresh.length > 0 && (
        <Alert role="alert"><AlertDescription>{COPY.refreshFailed(stamp(earliestIso(failedRefresh.map((q) => q.data?.envelope.computed_at))))}{retryButton(failedRefresh)}</AlertDescription></Alert>
      )}
      <FeedIssuesNote issues={issues} />
      {late.length > 0 && <Alert role="status"><AlertDescription>{lateHours != null ? <ConfigText text={COPY.lateAfter(stamp(earliestIso(late.map((g) => g.envelope.computed_at))), lateHours)} /> : COPY.late(stamp(earliestIso(late.map((g) => g.envelope.computed_at))))}</AlertDescription></Alert>}
      {basis === "current_inventory_cogs" && xero?.stale && <Alert role="status"><AlertDescription>{xero.latest ? COPY.oldXero(humanDate(xero.latest)) : COPY.noXero}</AlertDescription></Alert>}
      {oldFeed && <Alert role="status"><AlertDescription>{oldFeed.latest ? COPY.oldFeed(daysSince(oldFeed.latest, new Date()), humanDate(oldFeed.latest)) : COPY.noFeed}</AlertDescription></Alert>}
      {noneMarkets.map((m) => <Alert key={m} role="status"><AlertDescription>{COPY.marketMissing(MARKET_LABEL[m])}</AlertDescription></Alert>)}
      {/* Before the first change every figure is today's, so the banner is its first sentence alone (review round 2). After it, a
          chips or view read that answered another day names that day (C1 v1.25). */}
      {asOf !== null && (
        <Alert role="status"><AlertDescription>
          {inForceFrom === null ? AS_OF_COPY.bannerHead(humanDate(asOf), null)
            : [AS_OF_COPY.bannerHead(humanDate(asOf), humanDate(inForceFrom)), movedLine(grids), AS_OF_COPY.bannerCogs(humanDate(runDate)),
              ...(chipsDay === null ? [] : [AS_OF_COPY.chipsOn(humanDate(chipsDay))]), ...(viewDay === null ? [] : [AS_OF_COPY.viewOn(label, humanDate(viewDay))])].join(" ")}
        </AlertDescription></Alert>
      )}
      {grids.filter((g) => (g.envelope.incomplete_dates ?? []).length > 0).map((g) => (
        <Alert key={`incomplete-${g.grid.market}`} role="alert"><AlertDescription>
          {AS_OF_COPY.incomplete(MARKET_LABEL[g.grid.market], (g.envelope.incomplete_dates ?? []).map(humanDate).join(", "))}
        </AlertDescription></Alert>
      ))}
      {isPartnerView(view) && <Alert role="status"><AlertDescription>{ORDER_TYPE_COPY.partnerBanner(label)}</AlertDescription></Alert>}
      {/* The Subscription view says what it reads and how many of the region's rows it leaves out (C1 v1.35): every store and
          marketplace row, whatever the filters show, a ship country counting with its store. It waits until every market on screen
          has answered or failed, so the count is said once and does not change as the markets arrive (review round 2). */}
      {view === "subscription" && answered && viewQueries.every((q) => q.data || q.error) && <Alert role="status" data-view-banner><AlertDescription>{ORDER_TYPE_COPY.subscriptionBanner(notViewedCount(grids.flatMap((g) => g.grid.rows.filter((r) => r.grain !== "ship_country").map((r) => viewLookup(views, g.grid.market, skuOf(r), placeOf(r))))))}</AlertDescription></Alert>}
      {views && markets.map((m, i) => (viewQueries[i].error && !viewQueries[i].data ? (
        <Alert key={`view-${m}`} role="alert"><AlertDescription>
          {ORDER_TYPE_COPY.failed(label, MARKET_LABEL[m], viewRefusal(viewQueries[i].error, areaName))}
          <Button size="sm" variant="outline" className="ml-2" data-nonmutating onClick={() => void viewQueries[i].refetch()}>{COPY.retry}</Button>
        </AlertDescription></Alert>
      ) : null))}
      <p className="text-sm font-semibold text-ks-ink">{basisLine}</p>
      {region === "all" && <p className="text-sm text-ks-grey-1">{COPY.allRegionsNote}</p>}
      <CommitBar runs={runs} markets={markets} areaName={areaName} onCommitted={(committed, rrps) => { setApplied({ committed, rrps }); refreshOverrides(client); }} asOf={asOf} basis={basis} />
      {applied && <ApplyPanel committed={applied.committed} rrps={applied.rrps} onDone={() => setApplied(null)} />}
      <OverridesSection areaName={areaName} />
      <p className="text-sm text-ks-ink" aria-live="polite">{caption}</p>
      <BandLegend />
      <DataTable
        columns={columns}
        rows={page.rows}
        page={{ pageIndex: page.pageIndex, pageSize: PAGE_SIZE, rowCount: groups.length }}
        onPageChange={(next) => set({ pp_page: next || undefined })}
        caption={caption}
        wrap
        cards
        emptyMessage={active > 0 ? COPY.noMatch : COPY.noRows}
        rowClassName={(v) => (v.child ? "bg-ks-purple-4" : undefined)}
        onRowClick={(v) => openRow(rowKey(v.row))}
      />
      {lastPass && lastPass.skipped_count > 0 && (
        <div className="flex flex-col gap-1 text-sm" data-propose-skipped-line>
          <p className="text-ks-ink">
            {[PROPOSE_COPY.skippedLine(lastPass.skipped_count, lastPass.row_count), ...skippedGroups(lastPass, runs, placeNames).map((g) => `${g.label} (${g.count})`)].join(" · ")}
            <Button size="sm" variant="ghost" className="ml-2" aria-expanded={showSkipped} data-nonmutating onClick={() => setShowSkipped((x) => !x)}>
              {showSkipped ? PROPOSE_COPY.hideSkipped : PROPOSE_COPY.showSkipped}
            </Button>
          </p>
          {showSkipped && <SkippedList groups={skippedGroups(lastPass, runs, placeNames)} />}
        </div>
      )}
      {gate && <p className="text-xs text-ks-grey-1">{gateRule(gate)}</p>}
      {footer.length > 0 && <div className="flex flex-col gap-1 text-xs text-ks-grey-1">{footer.map((line) => <p key={line}>{line}</p>)}</div>}
      <p className="text-xs text-ks-grey-1" data-footnote>{COPY.footnote}</p>
      {proposeTo && (
        <ProposeSheet open={proposeOpen} scope={{ market: proposeTo, basis, channel: proposeChannel(values.channel), ticked: [...ticks.values()] }} runs={runs} placeNames={placeNames} areaName={areaName}
          onClose={() => { setProposeOpen(false); window.setTimeout(() => proposeButton.current?.focus(), 0); }}
          onSaved={(answer) => { setLastPass(answer); setTicks(new Map()); }} />
      )}
      <RowDetailSheet grids={grids} rowKey={typeof search.pp_row === "string" ? search.pp_row : null} onClose={closeRow} edit={{ runs, lastRuns, areaName, carry: carry ?? undefined, overrides }} rrps={rrps} evals={evals} customers={customers} target={config.data ? targetSetting : undefined}
        floors={config.data?.floors} links={config.data?.currency_links} views={view === null || views === null ? undefined : { view, label, reads: views }}
        asOf={asOf === null ? undefined : { date: asOf, runDate }} ads={ads}
        pick={{ runDate, onPick: (date, wanted) => readThenPick(client, markets, basis, date, wanted, pickDate) }} />
    </div>
  );
}

/** The Product · Marketplace cell (CLB-022): the name, which opens the drawer and is the anchor focus returns to, and the row's place.
 * A store row with countries in the feed keeps its Show / Hide countries button (E1); it toggles without opening the drawer. */
function ProductCell({ v, onOpen, onToggle }: { v: VisibleRow; onOpen: () => void; onToggle: () => void }) {
  const row = v.row;
  // A ship-country row's line opens its own drawer and is its focus anchor, as the product name is a store's.
  if (v.child) {
    return (
      <button type="button" className="pl-6 text-left text-ks-grey-1 hover:underline" title={COPY.detail} data-row-key={rowKey(row)} data-nonmutating
        onClick={(e) => { e.stopPropagation(); onOpen(); }}>ships to <b className="text-ks-ink">{countryName(row.location)}</b> · {orders(row.orders)}</button>
    );
  }
  const Chevron = v.expanded ? ChevronDown : ChevronRight;
  const place = row.grain === "marketplace" ? (row.marketplace_name ?? row.location ?? ABSENT) : (STORE_LABEL[row.channel_location] ?? `${MARKET_LABEL[v.market]} store`);
  return (
    <div>
      <button type="button" className="text-left font-semibold text-ks-ink hover:underline" title={COPY.detail} data-row-key={rowKey(row)} data-nonmutating
        onClick={(e) => { e.stopPropagation(); onOpen(); }}>{row.product}</button>
      {row.variant && <div className="text-xs text-ks-ink" data-row-variant>{row.variant}</div>}
      <div className="text-xs text-ks-grey-1">{place}</div>
      {row.grain === "store" && (row.weighting?.countries_total ?? 0) > 0 && (
        <Button size="sm" variant="outline" className="mt-1" aria-expanded={v.expanded} data-nonmutating onClick={(e) => { e.stopPropagation(); onToggle(); }}>
          <Chevron aria-hidden className="size-4 text-ks-purple" />{v.expanded ? COPY.hideCountries : COPY.showCountries}
        </Button>
      )}
    </div>
  );
}

/** The Price cell (CLB-022): the price in force and nothing under it; a row with no price names that, as the cell did. */
function PriceCell({ row }: { row: GridRow }) {
  if (!row.price) return <Badge variant="destructive">{MISSING_LABEL.no_price}</Badge>;
  return <span className="font-bold tabular-nums">{money(row.price.value, row.price.currency)}</span>;
}
